import { useTranslation } from 'react-i18next';
import { Clock, TrendingUp } from 'lucide-react';
import { simulateCashFlow } from './dealCalc';

export default function InvestmentTimeline({ deal }) {
  const { t } = useTranslation();

  function yearsToPositive(overrides = {}, annualGrowth = 0) {
    let cf = simulateCashFlow(deal, overrides);
    if (cf >= 0) return 0;
    for (let y = 1; y <= 30; y++) {
      const inc = deal.annual_income * Math.pow(1 + annualGrowth / 100, y);
      cf = simulateCashFlow(deal, { ...overrides, annual_income: inc });
      if (cf >= 0) return y;
    }
    return null;
  }

  const rows = [
    { key: 'current', label: t('decision.currentPath'), years: yearsToPositive(), color: 'bg-blue-500' },
    { key: 'growth', label: t('decision.withRentGrowth'), years: yearsToPositive({}, 3), color: 'bg-emerald-500' },
    { key: 'price', label: t('decision.atSuggestedPrice'), years: yearsToPositive({ purchase_price: deal.suggested_offer_price }), color: 'bg-violet-500' },
    { key: 'rate', label: t('decision.atLowerRate'), years: yearsToPositive({ interest_rate: Math.max(2, deal.interest_rate - 1) }), color: 'bg-amber-500' },
  ];

  if (deal.monthly_cash_flow >= 0) {
    return (
      <div data-testid="investment-timeline" className="bg-white dark:bg-[#0F1419] border border-slate-200 dark:border-white/[0.06] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-violet-500" />
          <h3 className="font-semibold text-sm text-slate-900 dark:text-white">{t('decision.timeline')}</h3>
        </div>
        <div className="flex items-center gap-2 text-sm text-emerald-500 font-semibold">
          <TrendingUp className="w-4 h-4" /> {t('decision.immediately')}
        </div>
      </div>
    );
  }

  const maxY = Math.max(...rows.map(r => r.years || 30));

  return (
    <div data-testid="investment-timeline" className="bg-white dark:bg-[#0F1419] border border-slate-200 dark:border-white/[0.06] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-violet-500" />
        <h3 className="font-semibold text-sm text-slate-900 dark:text-white">{t('decision.timeline')}</h3>
      </div>
      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.key}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500 dark:text-slate-400">{r.label}</span>
              <span className="font-bold text-slate-900 dark:text-white tabular-nums">
                {r.years === null ? t('decision.never') : r.years === 0 ? t('decision.immediately') : `${r.years} ${t('decision.years')}`}
              </span>
            </div>
            <div className="h-2 bg-slate-100 dark:bg-white/[0.06] rounded-full overflow-hidden">
              <div className={`h-full ${r.color} rounded-full transition-all duration-700`}
                style={{
                  width: r.years === null ? '100%' : `${Math.max(4, ((r.years || 0.5) / maxY) * 100)}%`,
                  opacity: r.years === null ? 0.25 : 1,
                }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
